import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {Button, Form, Modal} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import NginxService from '../http/NginxService';
import { NginxDomainConf } from '../entities/NginxDomainConf';
import { NginxLocationConf, ProxyPassType, ProxyPassTypeExt } from '../entities/NginxLocationConf';
import LocationForm from '../components/LocationForm';
import GrayReleaseServerPicker from '../components/GrayReleaseServerPicker';
import './ConfEditPage.css';
import {
    NginxLocationConfiguration,
} from "../entities/NginxLocationConfiguration";
import { toast } from "react-toastify";

const ConfEditPage: React.FC = () => {
    document.title = '编辑配置文件';
    const navigate = useNavigate();
    const { domainName } = useParams<{ domainName: string }>();
    const locationHook = useLocation();
    const [nginxConf, setNginxConf] = useState<NginxDomainConf | null>(locationHook.state?.conf);
    const [locationsList, setLocationsList] = useState<NginxLocationConf[]>([]);
    const [editingLocation, setEditingLocation] = useState<NginxLocationConf | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState<string>('');
    const [isGrayReleaseModalVisible, setIsGrayReleaseModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isGrayReleaseLoading, setIsGrayReleaseLoading] = useState(false);
    const [form] = Form.useForm<NginxLocationConf>();

    const isDirty = () => {
        return JSON.stringify(locationsList) !== JSON.stringify(nginxConf?.locations);
    };

    useEffect(() => {
        if (!domainName) {
            toast.error("链接错误！");
            return;
        }
        NginxService.getConf(domainName!).then((c) => {
            if (c == null) {
                toast.error("站点不存在！自动返回首页");
                navigate("/conf/list");
                return;
            }
            setNginxConf(c);
            setLocationsList(c.locations);
        });
    }, [domainName]);

    // 处理浏览器刷新或关闭时的提示
    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isDirty()) {
            e.preventDefault();
            e.returnValue = '';
        }
    };

    const goBack = () => {
        if (isDirty() && !window.confirm("你有未保存的更改，确定要离开吗？")) {
            return;
        }
        navigate(-1);
    }

    const handleEditLocation = (index: number) => {
        const conf = locationsList[index];
        form.setFieldsValue(conf);
        setEditingLocation(conf);
        setIsModalVisible(true);
    };

    const duplicate = (index: number) => {
        const conf = { ...locationsList[index], id: 0, sequence: 0 };
        form.setFieldsValue(conf);
        setEditingLocation(conf);
        setIsModalVisible(true);
    }

    const handleAddLocation = () => {
        form.setFieldsValue({
            path: '/',
            proxyPassType: ProxyPassType[ProxyPassType.APP_KEY],
            proxyPass: '',
            proxyHeaderHost: '',
            configurations: [],
            description: null,
            sequence: 0,
            staticFile: "~ ^/(robots.txt|manifest.json|asset-manifest.json|favicon.ico)",
            staticPath: "~ ^/(static|images)/"
        });
        setEditingLocation(null)
        setIsModalVisible(true);
    };

    const handleSaveLocation = (location: NginxLocationConf) => {
        if (editingLocation == null || editingLocation.sequence === 0) {
            if (locationsList.some((loc) => loc.path === location.path)) {
                toast.error('路径已存在！');
                return;
            }
            location.sequence = locationsList.length + 1;
            setLocationsList([...locationsList, location]);
        } else {
            const updated = locationsList.map((loc) =>
                loc.sequence === location.sequence ? location : loc
            );
            setLocationsList([...updated]);
        }
        setIsModalVisible(false);
    };

    const minimizeLocationForm = async () => {
        const fields = await form.getFieldsValue();
        const isDirty = Object.keys(fields).some(key => JSON.stringify(fields[key]) !== JSON.stringify(editingLocation?.[key]));
        if (isDirty && !window.confirm("你有未保存的更改，确定要离开吗？")) {
            return;
        }
        cancelEditLocationForm();
    };

    const cancelEditLocationForm = () => {
        setIsModalVisible(false);
    };

    const handleDeleteLocation = (index: number) => {
        const updated = locationsList.filter((_, i) => i !== index);
        setLocationsList(updated);
    };

    const handleSaveConf = async (grayRelease: boolean, grayReleaseIps?: string[]) => {
        setIsLoading(true);
        const updatedConf = { domainName: nginxConf!.domainName, locations: locationsList, grayRelease: grayRelease, grayReleaseIps: grayReleaseIps };
        await NginxService.updateConf(updatedConf).then((response) => {
            if (response.code !== 200) {
                return;
            }
            if (grayRelease) {
                toast.success('灰度发布成功！');
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.location.reload();
            } else {
                toast.success('保存成功！');
                navigate(-1);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const moveLocationUp = (index: number) => {
        if (index === 0) {
            return;
        }
        const newLocationsList = [...locationsList];
        [newLocationsList[index - 1], newLocationsList[index]] = [newLocationsList[index], newLocationsList[index - 1]];
        setLocationsList(newLocationsList);
    };

    const moveLocationDown = (index: number) => {
        if (index === locationsList.length - 1) {
            return;
        }
        const newLocationsList = [...locationsList];
        [newLocationsList[index + 1], newLocationsList[index]] = [newLocationsList[index], newLocationsList[index + 1]];
        setLocationsList(newLocationsList);
    };

    const preview = async () => {
        if (!nginxConf) {
            return;
        }
        const previewData = await NginxService.previewConf({ domainName: nginxConf.domainName, locations: locationsList });
        setPreviewContent(previewData);
        setIsPreviewModalVisible(true);
    };

    const handleGrayRelease = () => {
        setIsGrayReleaseModalVisible(true);
    };

    const handleGrayReleaseConfirm = async (selectedIps: string[]) => {
        if (!nginxConf) {
            return;
        }
        if (selectedIps && selectedIps.length === 0) {
            toast.error('请选择灰度发布的服务器！');
            return;
        }
        setIsGrayReleaseLoading(true);
        setIsGrayReleaseModalVisible(false);
        if (nginxConf.locked) {
            NginxService.grayRelease({ domainName: nginxConf.domainName, grayReleaseIps: selectedIps })
                .then(() => {
                    toast.success('灰度发布成功！');
                    window.removeEventListener('beforeunload', handleBeforeUnload);
                    window.location.reload();
                }).finally(() => {
                setIsGrayReleaseLoading(false);
            });
        } else {
            handleSaveConf(true, selectedIps).finally(() => {
                setIsGrayReleaseLoading(false);
            });
        }
    };

    if (!nginxConf) {
        return <div>加载中...</div>;
    }

    return (
        <div className="conf-edit-page">
            <div className="conf-edit-header">
                <div className="conf-edit-header-title">
                    <Button type="link" style={{ color: 'black'}}
                            icon={<ArrowLeftOutlined />} onClick={goBack} />
                    <span>站点：{nginxConf?.domainName}</span>
                </div>
                <div className="header-buttons">
                    <Button onClick={preview}>预览</Button>
                    <Button onClick={() => handleSaveConf(false)} disabled={isLoading}>全量发布</Button>
                    <Button type="primary" onClick={handleGrayRelease} disabled={isLoading}>灰度发布</Button>
                </div>
            </div>
            <div className="site-info">
                <h3>站点信息</h3>
                <div>
                    环境：<strong>{nginxConf?.environment}</strong>
                </div>
                <div>
                    CDN站点：<strong>{nginxConf?.cdnSite ? '是' : '否'}</strong>
                </div>
                <div>
                    锁定：<strong>{nginxConf?.locked ? '是' : '否'}</strong>
                </div>
            </div>
            <div className="conf-container">
                <div className="form-layout">
                    <h3>映射规则</h3>
                    {locationsList.map((loc, index) => (
                        <div key={index} className="location-item">
                            <div className="location-item-header">
                                <h4>规则{index + 1}</h4>
                                <div className="location-item-actions">
                                    {locationsList.length > 1 && index !== 0 && (
                                        <Button onClick={() => moveLocationUp(index)}>上移</Button>
                                    )}
                                    {locationsList.length > 1 && index < locationsList.length - 1 && (
                                        <Button onClick={() => moveLocationDown(index)}>下移</Button>
                                    )}
                                    <Button onClick={() => duplicate(index)}>复制</Button>
                                    <Button color="blue" variant="outlined" onClick={() => handleEditLocation(index)}>编辑</Button>
                                </div>
                            </div>
                            <div className="location-item-content">
                                <div>
                                    路径：<strong>{loc.path}</strong>
                                </div>
                                {loc.description && (
                                    <div>
                                        描述：<strong>{loc.description}</strong>
                                    </div>
                                )}
                                <div>
                                    代理方式：<strong>{ProxyPassTypeExt.getDescription(ProxyPassTypeExt.fromString(loc.proxyPassType))}</strong>
                                </div>
                                {
                                    loc.proxyPassType === ProxyPassType[ProxyPassType.APP_KEY]  && (
                                        <div>
                                            服务AppKey：<strong>{loc.proxyPass}</strong>
                                        </div>
                                    )
                                }
                                {
                                    loc.proxyPassType === ProxyPassType[ProxyPassType.FRONTEND] && (
                                        <div>
                                            前端AppKey：<strong>{loc.proxyPass}</strong>
                                        </div>
                                    )
                                }
                                {
                                    loc.proxyPassType === ProxyPassType[ProxyPassType.RETURN] && (
                                        <div>
                                            返回值：<strong>{loc.proxyPass}</strong>
                                        </div>
                                    )
                                }
                                {
                                    loc.proxyPassType === ProxyPassType[ProxyPassType.REWRITE] && (
                                        <div>
                                            重定向地址：<strong>{loc.proxyPass}</strong>
                                        </div>
                                    )
                                }
                                {loc.configurations.map((config) => new NginxLocationConfiguration(config.type, config.value))
                                    .map((config) => (
                                        <div key={config.type}>
                                            {config.getTypeDescription()}：
                                            <strong>{config.getDisplayValue()}</strong>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    ))}
                </div>
                <div className="add-location-button">
                    <Button color="blue" variant="outlined" onClick={handleAddLocation}>+ 添加 Location</Button>
                </div>
            </div>
            <Modal
                width="75%"
                title={editingLocation == null ? "添加映射规则" : "编辑映射规则"}
                open={isModalVisible}
                onCancel={minimizeLocationForm}
                footer={[
                    <Button onClick={minimizeLocationForm}>取消</Button>,
                    editingLocation && <Button type="default" danger onClick={() => handleDeleteLocation(locationsList.indexOf(editingLocation!))}>删除</Button>,
                    <Button type="primary" onClick={() => form.submit()} loading={isLoading}>保存</Button>
                ]}>
                <LocationForm
                    form={form}
                    conf={editingLocation}
                    onSave={handleSaveLocation}
                />
            </Modal>
            <Modal
                width="75%"
                title="预览配置"
                open={isPreviewModalVisible}
                onCancel={() => setIsPreviewModalVisible(false)}
                footer={null}
            >
                <pre>{previewContent}</pre>
            </Modal>
            <GrayReleaseServerPicker
                domainName={nginxConf!.domainName}
                visible={isGrayReleaseModalVisible}
                onCancel={() => setIsGrayReleaseModalVisible(false)}
                onConfirm={handleGrayReleaseConfirm}
                loading={isGrayReleaseLoading}
            />
        </div>
    );
};

export default ConfEditPage;
